<template>
  <Teleport to="body">
    <div class="toast-container position-fixed top-0 end-0 p-3">
      <BToast
        v-for="toast in toasts"
        :key="toast.id"
        :model-value="true"
        @update:model-value="removeToast(toast.id)"
        @onClose="removeToast(toast.id)"
        :variant="toast.type"
        :title="toast.title"
        header-class="toast-notification-header"
        class="custom-toast"
        :class="getToastClass(toast.type)"
      >
        <template v-if="toast.component">
          <component :is="toast.component" />
        </template>
        <template v-else>
          <button
            type="button"
            class="toast-btn-close close position-absolute"
            @click="removeToast(toast.id)"
            aria-label="Close"
          >×</button>
          <div class="notification-template position-relative py-2 px-1">
            <div class="d-flex align-items-center">
              <span class="me-2">
                <i class='alert-icon' :class="toast.icon"></i>
              </span>
              <div>
                <div v-if="notificationTitle && jsonMessages.length" class="toast-title">
                  {{ notificationTitle }}
                </div>
                <div
                  v-for="(message, index) in jsonMessages"
                  v-if="jsonMessages.length"
                  :key="`json-${index}`"
                  class="notification-message"
                >
                  {{ message }}
                </div>
                <div v-else-if="textMessage" class="notification-message">
                  {{ textMessage }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </BToast>
    </div>
  </Teleport>
</template>

<script setup>
import {ref, onMounted, onUnmounted} from 'vue'
import { BToast } from 'bootstrap-vue-next'
import {useNotificationListener} from "@/plugins/notify";
import {useReactiveStoreGetters} from "@/composables/useStore";

const {
  notificationTextMessage: textMessage,
  notificationJsonMessage: jsonMessages,
  notificationTitle,
} = useReactiveStoreGetters([
  'notificationTextMessage',
  'notificationJsonMessage',
  'notificationTitle',
]);


const toasts = ref([])
let toastCounter = 0

const addToast = (notification) => {
  const id = toastCounter++
  toasts.value.push({
    id,
    ...notification
  })

  setTimeout(() => {
    removeToast(id)
  }, 5000)
}

const removeToast = (id) => {
  const index = toasts.value.findIndex(toast => toast.id === id)
  if (index !== -1) {
    toasts.value.splice(index, 1)
  }
  toastCounter--;
}

const { subscribe } = useNotificationListener()
let unsubscribe

onMounted(() => {
  unsubscribe = subscribe(addToast)
})

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe()
  }
})

const getToastClass = (type) => {
  return {
    'toast-success': type === 'success',
    'toast-danger': type === 'danger',
    'toast-info': type === 'info',
    'toast-warning': type === 'warning'
  }
}
</script>

<style lang="scss">
.toast-notification-header {
  display: none !important;
}

.custom-toast {
  min-width: 300px;
  margin-bottom: 0.5rem;
  border-radius: 4px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s ease-out;
  border: none !important;
  padding: 0 !important;

  &.toast-success {
    .notification-template {
      color: #229863 !important;
    }

    .alert-icon {
      color: #229863 !important;
    }
  }

  &.toast-info {
    .notification-template {
      color: #3091b2 !important;
    }

    .alert-icon {
      color: #3091b2 !important;
    }
  }

  &.toast-danger {
    .notification-template {
      color: #b33c12 !important;
    }

    .alert-icon {
      color: #b33c12 !important;
    }
  }
}

.toast-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.toast-title {
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.2;
}

.notification-template {
  padding-right: 2rem !important; // Make space for close button
}

.toast-btn-close {
  font-size: 21px;
  font-weight: 700;
  opacity: 0.5;
  background: none;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem 0.75rem;
  border: 0;
  z-index: 1;

  &:hover {
    opacity: 1;
  }
}

.notification-message {
  margin-bottom: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.alert-icon {
  font-size: 2rem;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
